import React, { useCallback, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Spinner,
  Stack,
  Text,
} from '@thirstycamel/ui'

import { useAuth } from '../context/AuthContext'

type AuthGuardProps = {
  children: React.ReactNode
}

const getPushTokenFromUrl = () => {
  if (typeof window === 'undefined') {
    return null
  }
  return new URLSearchParams(window.location.search).get('pushToken')
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  if (typeof window === 'undefined') {
    return <>{children}</>
  }

  const { payload, loading, error, validate, token, setToken } = useAuth()
  const router = useRouter()
  const queryPushToken = typeof router.query.pushToken === 'string' ? router.query.pushToken : null
  const lastValidatedTokenRef = useRef<string | null>(null)

  useEffect(() => {
    const pushToken = queryPushToken || getPushTokenFromUrl()
    const nextToken = pushToken || token

    if (!nextToken || nextToken === lastValidatedTokenRef.current) {
      return
    }

    if (pushToken) {
      setToken(pushToken)
      if (queryPushToken) {
        const { pushToken: _pushToken, ...restQuery } = router.query
        router.replace({ pathname: router.pathname, query: restQuery }, undefined, {
          shallow: true,
        })
      } else if (getPushTokenFromUrl()) {
        const url = new URL(window.location.href)
        url.searchParams.delete('pushToken')
        window.history.replaceState({}, '', `${url.pathname}${url.search}`)
      }
    }

    lastValidatedTokenRef.current = nextToken
    validate(nextToken)
  }, [queryPushToken, token, setToken, validate, router.pathname])

  const handleRetry = useCallback(() => {
    if (loading) return
    const nextToken = queryPushToken || getPushTokenFromUrl() || token
    if (nextToken) {
      lastValidatedTokenRef.current = null
      validate(nextToken)
    }
  }, [queryPushToken, loading, token, validate])

  if (loading) {
    return (
      <Stack align="center" justify="center" minH="60vh" spacing={3}>
        <Spinner />
        <Text color="gray.600">Loading Reports..</Text>
      </Stack>
    )
  }

  if (!payload) {
    return (
      <Stack px={5} py={10} spacing={4}>
        <Alert status="error" variant="left-accent">
          <AlertIcon />
          <Stack spacing={1}>
            <AlertTitle>Invalid Session</AlertTitle>
            <AlertDescription>
              Your login could not be verified. Please relaunch Reporting from the Portal or Milie.
              <br />
              {error && <Text color="red">{error}</Text>}
            </AlertDescription>
          </Stack>
        </Alert>
        {(queryPushToken || getPushTokenFromUrl() || token) && (
          <Button onClick={handleRetry} width={['100%', 'auto']} alignSelf="flex-start">
            Retry validation
          </Button>
        )}
      </Stack>
    )
  }

  return <>{children}</>
}

export default AuthGuard
